/**
 * # AnchoredFixedPosition
 *
 * A component that positions its children relative to a target element using the Popper library.
 * A wrapper implementation of the `react-popper` library. Please refer to the [React Popper Docs](https://popper.js.org/docs/v2/) for more typing details.
 *
 * ## Props
 *
 * - `className?: string`: Additional CSS class name for the component.
 * - `target?: HTMLElement`: The target element to position the children relative to.
 * - `eventsEnabled?: boolean`: Whether scroll/resize listeners are enabled for the Popper instance.
 * - `children?: React.ReactNode`: The content to be rendered inside the component.
 * - `placement?: Placement`: The placement of the component relative to the target element.
 * - `usePortal?: boolean`: Whether to render the component inside a portal. If `true`, effectively takes element outside of its natural DOM-placement and overlays it above the document.
 * - `arrowClassName?: string`: When provided, renders a Popper arrow element with this class inside the popper node. Popper's arrow modifier positions it along the popper edge so it points at the target.
 * - `modifiers?`: Popper modifiers. Accepts the legacy v1 object form (translated to the v2 array internally) or a v2 array.
 *
 * ## Usage
 *
 * ```tsx
 * import AnchoredFixedPosition from 'path/to/AnchoredFixedPosition';
 *
 * const ExampleComponent = () => {
 *   const targetRef = useRef(null);
 *
 *   return (
 *     <div>
 *       <button ref={targetRef}>Target Element</button>
 *       <AnchoredFixedPosition target={targetRef.current} placement="bottom">
 *         <div>Positioned Content</div>
 *       </AnchoredFixedPosition>
 *     </div>
 *   );
 * };
 * ```
 */
import React, {useEffect} from 'react';
import type {Modifier, Placement, State} from '@popperjs/core';
import {Popper} from 'react-popper';
import {AppPortal} from 'client/Portals/AppPortal/AppPortal.react';

import type {ModifierInput} from '../popper/popperCompat';
import {toModifierArray} from '../popper/popperCompat';

interface AnchoredFixedPositonProps extends PropsWithClassNameOptional {
  children?: React.ReactNode | (() => React.ReactNode);
  target?: HTMLElement;
  eventsEnabled?: boolean;
  placement?: Placement;
  usePortal?: boolean;
  arrowClassName?: string;
  modifiers?: ModifierInput;
}

/**
 * Nearest scrollable ancestor of `el`, or `undefined` if none exists before the
 * document body. Used to bound a fixed-strategy popper to the container its target
 * scrolls within: popper v1's `preventOverflow` boundary defaulted to this
 * 'scrollParent', but v2 defaults to the popper's clipping parents — which for our
 * portaled, fixed popper is the viewport — so the boundary must be passed explicitly.
 */
export function getScrollableAncestor(el: HTMLElement | null | undefined): HTMLElement | undefined {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    const {overflow, overflowX, overflowY} = window.getComputedStyle(node);
    if (/auto|scroll|overlay/.test(`${overflow}${overflowX}${overflowY}`)) {
      return node;
    }
    node = node.parentElement;
  }
  return undefined;
}

/**
 * How far (px) the arrow must stay from either end of the popper. Popper's own
 * `arrow` modifier only clamps the arrow's layout box to the popper's bounding box,
 * but our caret is a rotated square whose corners overhang that box (~2px for a
 * 10px caret), so a flush arrow renders past the popper's edge and onto its
 * rounded corners. 8px covers the overhang plus the tooltip's 4px border radius.
 */
export const ARROW_EDGE_INSET = 8;

/**
 * Custom Popper modifier that re-clamps `modifiersData.arrow` after the built-in
 * `arrow` modifier has positioned it, keeping the arrow at least ARROW_EDGE_INSET
 * away from both ends of the popper. Runs in the `main` phase after `arrow` (which
 * itself runs after `flip`), so the clamp survives placement flips.
 */
export const keepArrowWithinBounds: Modifier<'keepArrowWithinBounds', Record<string, never>> = {
  name: 'keepArrowWithinBounds',
  enabled: true,
  phase: 'main',
  requires: ['arrow'],
  fn({state}: {state: State}) {
    const arrowData = state.modifiersData.arrow;
    const arrowElement = state.elements.arrow;
    if (!arrowData || !arrowElement) {
      return;
    }

    // The arrow slides along the popper's horizontal edge for top/bottom
    // placements and along its vertical edge for left/right placements.
    const isVertical = ['left', 'right'].includes(state.placement.split('-')[0]);
    const axis = isVertical ? 'y' : 'x';
    const length = isVertical ? 'height' : 'width';
    const arrowSize = isVertical ? arrowElement.offsetHeight : arrowElement.offsetWidth;
    const max = state.rects.popper[length] - arrowSize - ARROW_EDGE_INSET;

    // Popper too small to inset from both ends — keep Popper's own clamping.
    if (max < ARROW_EDGE_INSET) {
      return;
    }

    arrowData[axis] = Math.min(Math.max(arrowData[axis] ?? 0, ARROW_EDGE_INSET), max);
  }
};

/**
 * Re-anchors the popper when layout shifts without a scroll or resize event.
 * Popper only recomputes position on those events, so content that mounts or
 * grows after the initial computation — above the target, or inside it — leaves
 * the popper stranded at coordinates from the old layout (e.g. overlapping its
 * own target). A target that only *moves* resizes nothing itself, so the watch
 * spans the document body and every ancestor up to it: a sibling panel sliding
 * open narrows an ancestor, and the target travels with it.
 */
const UpdateOnLayoutShift = ({
  target,
  update
}: {
  target: HTMLElement;
  update: () => Promise<unknown>;
}) => {
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      update();
    });
    observer.observe(document.body);
    for (
      let node: HTMLElement | null = target;
      node && node !== document.body;
      node = node.parentElement
    ) {
      observer.observe(node);
    }
    return () => observer.disconnect();
  }, [target, update]);

  return null;
};

const AnchoredFixedPosition = ({
  target,
  children,
  className,
  placement = 'bottom',
  eventsEnabled,
  usePortal,
  arrowClassName,
  modifiers
}: AnchoredFixedPositonProps) => {
  if (!target) {
    return null;
  }

  const popperModifiers: Array<Partial<Modifier<string, object>>> = [
    ...toModifierArray(modifiers),
    ...(eventsEnabled !== undefined ? [{name: 'eventListeners', enabled: eventsEnabled}] : []),
    ...(arrowClassName ? [keepArrowWithinBounds] : [])
  ];

  const content = typeof children === 'function' ? children() : children;

  return (
    <AppPortal usePortal={usePortal} className={className}>
      <Popper
        referenceElement={target}
        strategy='fixed'
        placement={placement}
        modifiers={popperModifiers}
      >
        {({ref, style, placement: computedPlacement, arrowProps, update}) => (
          <div ref={ref} style={style} data-placement={computedPlacement}>
            {eventsEnabled !== false && <UpdateOnLayoutShift target={target} update={update} />}
            {content}
            {arrowClassName && (
              <div ref={arrowProps.ref} style={arrowProps.style} className={arrowClassName} />
            )}
          </div>
        )}
      </Popper>
    </AppPortal>
  );
};

export default AnchoredFixedPosition;
